import type { Breadcrumb, BrowserOptions } from "@sentry/react";

import { HANDSHAKE_QUERY_PARAM, PARENT_TOKEN_PARAM, TEACHER_EVALUATION_TOKEN_PARAM } from "const";

// AIDEV-NOTE: Přihlašovací tokeny (parent-token, authorization-token, teacher-evaluation-token)
// chodí v query stringu a z URL se mažou až po startu pageload spanu — do té doby stihnou
// projít přes browserTracingIntegration do popisů spanů (`browser.request`/`response`/…),
// `request.url` i breadcrumbs (zjištěno na stage 25. 9. 2026, 8 výskytů URL na jednu trasu).
// Session Replay tímhle krytý není — jde o vlastní nahrávání URL, rozhodnutí o replayi
// padlo jinde. [TD-427]
const TOKEN_PARAM_NAMES = [HANDSHAKE_QUERY_PARAM, PARENT_TOKEN_PARAM, TEACHER_EVALUATION_TOKEN_PARAM];

// Regexy se kompilují jednou při načtení modulu, ne při každém volání — redactUrlTokens
// běží na každém spanu/breadcrumbu/eventu. `(^|[?&#])` pokrývá i token na úplném
// začátku řetězce (holý query string bez URL), ne jen za oddělovačem. [TD-427]
const TOKEN_PARAM_PATTERNS = TOKEN_PARAM_NAMES.map(param => new RegExp(`(^|[?&#])(${param}=)[^&#\\s"]*`, "g"));

export const redactUrlTokens = (value: string): string =>
  TOKEN_PARAM_PATTERNS.reduce((result, pattern) => result.replace(pattern, "$1$2[Filtered]"), value);

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== "object" || value === null) return false;
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

// AIDEV-NOTE: `beforeSend`/`beforeSendTransaction`/`beforeSendSpan` dostávají eventy, které
// mohou nést cizí data (Redux `extra`, span attributes) — cykly i zmrazený (Immer) stav.
// Sestup proto jen do polí a plain objektů (žádné DOM prvky, třídní instance), s WeakSetem
// proti cyklům a přeskočením zmrazených kontejnerů — jinak hook spadne (přetečení zásobníku
// / TypeError na read-only vlastnost) dřív, než token stihne zamaskovat. Zmrazený kontejner
// se nechává beze změny, tokeny uvnitř něj tím pádem zůstávají — vědomé zbytkové riziko.
// `beforeBreadcrumb` tudy neprochází vůbec, viz `scrubBreadcrumbShallow` níž. [TD-427]
const scrubInPlace = (data: unknown, seen: WeakSet<object>): void => {
  if (Array.isArray(data)) {
    if (Object.isFrozen(data) || seen.has(data)) return;
    seen.add(data);
    data.forEach((item, index) => {
      if (typeof item === "string") {
        data[index] = redactUrlTokens(item);
      } else {
        scrubInPlace(item, seen);
      }
    });
    return;
  }
  if (!isPlainObject(data) || Object.isFrozen(data) || seen.has(data)) return;
  seen.add(data);
  for (const key of Object.keys(data)) {
    const value = data[key];
    if (typeof value === "string") {
      data[key] = redactUrlTokens(value);
    } else {
      scrubInPlace(value, seen);
    }
  }
};

export const scrubUrlTokens = <T>(data: T): T => {
  scrubInPlace(data, new WeakSet());
  return data;
};

// AIDEV-NOTE: `beforeBreadcrumb` běží ještě před normalizací SDK (`@sentry/core`
// `breadcrumbs.js`) — konzolové breadcrumby nesou surové argumenty `console.*`
// (`data.arguments`, `@sentry/browser` `integrations/breadcrumbs.js`), což může být
// cyklus, DOM prvek nebo zmrazený (Immer) Redux stav. `scrubUrlTokens` (hluboká
// rekurze) by na tom spadla dřív, než token stihne zamaskovat, proto se tu čistí jen
// mělce: `message` a přímé stringové hodnoty `data.url`/`data.from`/`data.to`.
// `data.arguments` se nesahá — zbytkové riziko, vědomé rozhodnutí. Nový objekt
// `data`, aby se neměnil (možná zmrazený) originál. [TD-427]
const BREADCRUMB_DATA_KEYS = ["url", "from", "to"] as const;

const scrubBreadcrumbShallow = (breadcrumb: Breadcrumb): Breadcrumb => {
  const scrubbed: Breadcrumb = { ...breadcrumb };
  if (typeof scrubbed.message === "string") {
    scrubbed.message = redactUrlTokens(scrubbed.message);
  }
  if (scrubbed.data && typeof scrubbed.data === "object") {
    const data: Record<string, unknown> = { ...scrubbed.data };
    for (const key of BREADCRUMB_DATA_KEYS) {
      const value = data[key];
      if (typeof value === "string") {
        data[key] = redactUrlTokens(value);
      }
    }
    scrubbed.data = data;
  }
  return scrubbed;
};

interface SentryEnv {
  DEV: boolean;
  VITE_ENV: string;
  VITE_SENTRY_DSN: string;
  VITE_GRAPHQL_ENDPOINT: string;
  VITE_GIT_SHA: string;
}

// Integrace (tracing, replay) sem nepatří — jejich konstrukce má vedlejší efekty a replay
// jde vytvořit jen jednou. Tady jsou jen hodnoty, které se liší podle prostředí. [TD-427]
export const buildSentryOptions = (env: SentryEnv): BrowserOptions => {
  const isProduction = env.VITE_ENV === "production";

  return {
    dsn: env.VITE_SENTRY_DSN,
    // `yarn start` čte .env.development se stejným DSN jako stage; bez tohohle by chyby
    // z lokálního vývoje padaly do Sentry jako stage.
    enabled: !env.DEV,
    // Stage frontendu má VITE_ENV "development"; backend stage hlásí jako "stage"
    // (SENTRY_ENVIRONMENT v deploy-service-stage.sh). Jeden název = jeden filtr v Sentry.
    environment: isProduction ? "production" : "stage",
    release: env.VITE_GIT_SHA,
    tracesSampleRate: isProduction ? 0.1 : 1.0,
    tracePropagationTargets: [env.VITE_GRAPHQL_ENDPOINT],
    replaysSessionSampleRate: isProduction ? 0.1 : 1,
    replaysOnErrorSampleRate: 1.0,
    beforeSend: event => scrubUrlTokens(event),
    // Transakce nese `spans` navíc oproti `Event` — v SDK 10.32.1 sem doputují i popisy
    // spanů `browser.*`, kde token unikal na stage 25. 9. 2026.
    beforeSendTransaction: event => scrubUrlTokens(event),
    // Web-vital spany (INP/CLS/LCP) jdou mimo `beforeSendTransaction` vlastní cestou
    // (`@sentry/core` envelope.js) — bez vlastního hooku by tudy token unikl.
    beforeSendSpan: span => scrubUrlTokens(span),
    beforeBreadcrumb: breadcrumb => scrubBreadcrumbShallow(breadcrumb),
  };
};
